import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Megaphone, Plus, Star, StarOff, Calendar, Eye, Image, Video, Link, X, ExternalLink, Trash2, Search, MessageCircle, Smile, SendHorizontal, ChevronDown, ChevronUp, Edit2, Users, Pin, FileText } from 'lucide-react';
import RichTextEditor from '../../components/RichTextEditor';
import { Button, Avatar, Modal, Input, Textarea, Select } from '../../components/UI';
import { useAuthStore } from '../../store/authStore';
import { announcementsApi } from '../../services/api';
import { Announcement, Comment, MediaFile } from '../../types';
import MentionInput from '../../components/MentionInput/MentionInput';
import MentionText from '../../components/MentionText/MentionText';
import FormattedText from '../../utils/formatText';
import styles from './Announcements.module.css';

const reactionEmojis = ['👍', '👏', '❤️', '🎉', '💡', '🔥', '⭐', '😊'];

type TimePeriod = '7' | '30' | '90' | 'custom';

const priorityOptions = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

const PRIORITY_CONFIG: Record<string, { label: string; color: string; bg: string; border: string; dot: string }> = {
  urgent: { label: 'Urgent', color: '#DC2626', bg: '#FEF2F2', border: '#FECACA', dot: '#EF4444' },
  high: { label: 'High', color: '#D97706', bg: '#FFFBEB', border: '#FDE68A', dot: '#F59E0B' },
  medium: { label: 'Medium', color: '#6366F1', bg: '#EEF2FF', border: '#C7D2FE', dot: '#818CF8' },
  low: { label: 'Low', color: '#64748B', bg: '#F8FAFC', border: '#E2E8F0', dot: '#94A3B8' },
};

const getAnnouncementKind = (title: string): { label: string; color: string; bg: string } => {
  if (title.includes('Welcome to Matasma') || title.includes('welcome')) return { label: 'ASSOCIATE UPDATE', color: '#0f7a68', bg: '#dff0ec' };
  if (title.startsWith('☕')) return { label: 'COFFEE SESSION', color: '#8B5CF6', bg: '#f3e8ff' };
  if (title.startsWith('📚')) return { label: 'KNOWLEDGE SERIES', color: '#3B82F6', bg: '#e0edfa' };
  if (title.startsWith('🏆')) return { label: 'ATHLETIC SERIES', color: '#10B981', bg: '#d1fae5' };
  if (title.includes('Townhall') || title.includes('Town Hall')) return { label: 'TOWN HALL', color: '#6366F1', bg: '#eef2ff' };
  if (title.includes('Potluck') || title.includes('Pictionary') || title.includes('Fun Friday') || title.includes('Dinner') || title.includes('MasterChef') || title.startsWith('🎉')) return { label: 'FUN FRIDAY', color: '#EC4899', bg: '#fdf2f8' };
  if (title.includes('Retreat') || title.includes('Synergy')) return { label: 'RETREAT', color: '#6366F1', bg: '#eef2ff' };
  if (title.includes('Teams Background') || title.includes('New Space') || title.startsWith('📢')) return { label: 'ORG UPDATE', color: '#64748B', bg: '#f1f5f9' };
  return { label: 'ORG UPDATE', color: '#64748B', bg: '#f1f5f9' };
};

const Announcements: React.FC = () => {
  const { user } = useAuthStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const highlightWidenTimeRef = useRef(0);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState<Announcement | null>(null);
  const [creating, setCreating] = useState(false);
  const [mentionInputKey, setMentionInputKey] = useState(0);
  const [error, setError] = useState('');
  const [newAnnouncement, setNewAnnouncement] = useState({
    title: '',
    content: '',
    priority: 'medium',
    is_pinned: false,
    link_url: '',
    link_title: '',
  });
  const [createMediaFiles, setCreateMediaFiles] = useState<MediaFile[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fileDocInputRef = useRef<HTMLInputElement>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Expanded card for description
  const [expandedCard, setExpandedCard] = useState<string | null>(null);

  // Time filter and search state
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('30');
  const [searchTerm, setSearchTerm] = useState('');
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [customDates, setCustomDates] = useState({ start: '', end: '' });

  // Comments and reactions state
  const [expandedComments, setExpandedComments] = useState<Set<string>>(new Set());
  const [commentsData, setCommentsData] = useState<Record<string, Comment[]>>({});
  const [commentsLoading, setCommentsLoading] = useState<Set<string>>(new Set());
  const [newComment, setNewComment] = useState<Record<string, string>>({});
  const [submittingComment, setSubmittingComment] = useState<Set<string>>(new Set());
  const [deletingComment, setDeletingComment] = useState<Set<string>>(new Set());
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editCommentText, setEditCommentText] = useState('');
  const [savingComment, setSavingComment] = useState(false);
  const [replyingToId, setReplyingToId] = useState<string | null>(null);
  const [hoverReactFor, setHoverReactFor] = useState<string | null>(null);
  const [hoverReactPos, setHoverReactPos] = useState<{ top: number; left: number } | null>(null);
  const hoverReactTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [replyText, setReplyText] = useState('');
  const commentInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const canCreate = user && ['superadmin', 'admin', 'executive', 'mentor'].includes(user.role);
  const canDelete = user?.role === 'superadmin';
  const canPin = user?.role === 'superadmin';
  const canEditAnnouncement = (announcement: Announcement) => {
    if (!user) return false;
    if (user.role === 'superadmin') return true;
    if (user.role === 'admin' && announcement.author_id === user.id) return true;
    return false;
  };

  // Edit announcement state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState<Announcement | null>(null);
  const [editFormData, setEditFormData] = useState({
    title: '',
    content: '',
    priority: 'medium',
    is_pinned: false,
    link_url: '',
    link_title: '',
  });
  const [editMediaFiles, setEditMediaFiles] = useState<MediaFile[]>([]);
  const [updating, setUpdating] = useState(false);
  const editFileInputRef = useRef<HTMLInputElement>(null);
  const editFileDocInputRef = useRef<HTMLInputElement>(null);

  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxMedia, setLightboxMedia] = useState<MediaFile[]>([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Viewed-by modal state
  const [showViewersModal, setShowViewersModal] = useState(false);
  const [viewersData, setViewersData] = useState<any[]>([]);
  const [viewersLoading, setViewersLoading] = useState(false);
  const [viewersTitle, setViewersTitle] = useState('');
  const [showReactionsModal, setShowReactionsModal] = useState(false);
  const [reactionsModalData, setReactionsModalData] = useState<any[]>([]);

  const canViewReaders = user?.role === 'superadmin';

  const handleViewReaders = useCallback(async (e: React.MouseEvent, announcement: Announcement) => {
    e.stopPropagation();
    setViewersTitle(announcement.title);
    setShowViewersModal(true);
    setViewersLoading(true);
    try {
      const response = await announcementsApi.getReaders(announcement.id);
      setViewersData(response.data);
    } catch (error) {
      console.error('Failed to fetch readers:', error);
      setViewersData([]);
    } finally {
      setViewersLoading(false);
    }
  }, [canViewReaders]);

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      const params: any = { page: 1 };
      if (timePeriod !== 'custom') {
        params.days = parseInt(timePeriod);
      } else if (customDates.start && customDates.end) {
        params.start_date = customDates.start;
        params.end_date = customDates.end;
      }
      const response = await announcementsApi.getAll(params);
      setAnnouncements(response.data.announcements);
    } catch (error) {
      console.error('Failed to fetch announcements:', error);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchAnnouncements(); }, [timePeriod]);

  // Handle highlight from notification click; widen the time filter if the
  // item isn't in the current window
  useEffect(() => {
    const id = searchParams.get('highlight');
    if (!id || loading) return;
    if (announcements.some(a => a.id === id)) {
      setHighlightId(id);
      setExpandedCard(id);
      // Open the comments section so a comment notification shows the comment
      setExpandedComments(prev => new Set(prev).add(id));
      if (!commentsData[id]) fetchComments(id);
      setTimeout(() => {
        const el = document.querySelector(`[data-announcement-id="${id}"]`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 200);
      // Clear the param so refreshing doesn't re-highlight
      setSearchParams({}, { replace: true });
      setTimeout(() => setHighlightId(null), 3000);
    } else if (timePeriod === '7' || timePeriod === '30') {
      highlightWidenTimeRef.current = Date.now();
      setTimePeriod('90');
    } else if (Date.now() - highlightWidenTimeRef.current > 1000) {
      // Widened window has finished loading and the item still isn't there — give up
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [announcements, loading, searchParams, setSearchParams, timePeriod]);

  // Filter announcements by search term
  const filteredAnnouncements = announcements.filter(a => {
    if (!searchTerm.trim()) return true;
    const search = searchTerm.toLowerCase();
    return (
      a.title?.toLowerCase().includes(search) ||
      a.content?.toLowerCase().includes(search) ||
      a.author_first_name?.toLowerCase().includes(search) ||
      a.author_last_name?.toLowerCase().includes(search)
    );
  });

  // Sidebar computed data
  const overviewStats = useMemo(() => {
    const total = announcements.length;
    const urgent = announcements.filter(a => a.priority === 'urgent').length;
    const pinned = announcements.filter(a => a.is_pinned).length;
    return [
      { label: 'Total', value: total, color: '#0C6B62' },
      { label: 'Urgent', value: urgent, color: '#DC2626' },
      { label: 'Pinned', value: pinned, color: '#D97706' },
    ];
  }, [announcements]);

  const recentActivity = useMemo(() => {
    return announcements.slice(0, 3);
  }, [announcements]);

  const handleTimePeriodChange = (period: TimePeriod) => {
    if (period === 'custom') {
      setShowCustomDatePicker(true);
    } else {
      setTimePeriod(period);
      setShowCustomDatePicker(false);
    }
  };

  const handleCustomDateApply = () => {
    if (customDates.start && customDates.end) {
      setTimePeriod('custom');
      setShowCustomDatePicker(false);
      fetchAnnouncements();
    }
  };

  const handleViewAnnouncement = async (announcement: Announcement) => {
    setSelectedAnnouncement(announcement);
    if (!announcement.is_read) {
      try {
        await announcementsApi.getById(announcement.id);
        setAnnouncements(prev =>
          prev.map(a => a.id === announcement.id ? { ...a, is_read: true, read_count: a.read_count + 1 } : a)
        );
      } catch (error) {
        console.error('Failed to mark announcement as read:', error);
      }
    }
  };

  const handleCreate = async () => {
    if (!newAnnouncement.title.trim() || !newAnnouncement.content.trim()) {
      setError('Please fill in title and content');
      return;
    }
    setCreating(true);
    setError('');
    try {
      const payload: any = {
        title: newAnnouncement.title,
        content: newAnnouncement.content,
        priority: newAnnouncement.priority,
        is_pinned: newAnnouncement.is_pinned,
      };
      if (createMediaFiles.length > 0) {
        payload.media_files = createMediaFiles;
        payload.media_url = createMediaFiles[0].url;
        payload.media_type = createMediaFiles[0].type;
      }
      if (newAnnouncement.link_url) {
        payload.link_url = newAnnouncement.link_url;
        payload.link_title = newAnnouncement.link_title || newAnnouncement.link_url;
      }
      await announcementsApi.create(payload);
      setShowCreateModal(false);
      setNewAnnouncement({ title: '', content: '', priority: 'medium', is_pinned: false, link_url: '', link_title: '' });
      setCreateMediaFiles([]);
      fetchAnnouncements();
    } catch (err: any) {
      console.error('Failed to create announcement:', err);
      setError(err.response?.data?.error || 'Failed to create announcement');
    } finally {
      setCreating(false);
    }
  };

  const handleMediaSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const remaining = 5 - createMediaFiles.length;
    if (remaining <= 0) { setError('Maximum 5 files allowed'); return; }
    const filesToProcess = Array.from(files).slice(0, remaining);
    let errorMsg = '';
    filesToProcess.forEach(file => {
      if (file.size > 5 * 1024 * 1024) { errorMsg = 'Each file must be less than 5MB'; return; }
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');
      const fileType = isImage ? 'image' : isVideo ? 'video' : 'file';
      const reader = new FileReader();
      reader.onloadend = () => {
        setCreateMediaFiles(prev => prev.length >= 5 ? prev : [...prev, { url: reader.result as string, type: fileType as any, name: file.name }]);
      };
      reader.readAsDataURL(file);
    });
    if (errorMsg) setError(errorMsg); else setError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemoveMedia = (index: number) => {
    setCreateMediaFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    if (hours < 1) return 'Just now';
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return formatDate(dateString);
  };

  const handleDeleteClick = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setDeleteId(id);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await announcementsApi.delete(deleteId);
      setAnnouncements(prev => prev.filter(a => a.id !== deleteId));
      setDeleteModalOpen(false);
      setDeleteId(null);
      if (selectedAnnouncement?.id === deleteId) {
        setSelectedAnnouncement(null);
      }
    } catch (error) {
      console.error('Failed to delete announcement:', error);
    } finally {
      setDeleting(false);
    }
  };

  const handleTogglePin = async (e: React.MouseEvent, announcement: Announcement) => {
    e.stopPropagation();
    try {
      const newPinnedState = !announcement.is_pinned;
      await announcementsApi.update(announcement.id, { is_pinned: newPinnedState });
      setAnnouncements(prev =>
        prev.map(a => a.id === announcement.id ? { ...a, is_pinned: newPinnedState } : a)
      );
      if (selectedAnnouncement?.id === announcement.id) {
        setSelectedAnnouncement({ ...selectedAnnouncement, is_pinned: newPinnedState });
      }
    } catch (error) {
      console.error('Failed to toggle pin:', error);
    }
  };

  // Edit announcement functions
  const handleEditClick = (e: React.MouseEvent, announcement: Announcement) => {
    e.stopPropagation();
    setEditingAnnouncement(announcement);
    setEditFormData({
      title: announcement.title || '',
      content: announcement.content || '',
      priority: announcement.priority || 'medium',
      is_pinned: announcement.is_pinned || false,
      link_url: announcement.link_url || '',
      link_title: announcement.link_title || '',
    });
    // Restore media files from announcement
    const existingFiles: MediaFile[] = [];
    if (announcement.media_files && announcement.media_files.length > 0) {
      existingFiles.push(...announcement.media_files);
    } else if (announcement.media_url) {
      existingFiles.push({ url: announcement.media_url, type: announcement.media_type || 'image' });
    }
    setEditMediaFiles(existingFiles);
    setSelectedAnnouncement(null);
    setShowEditModal(true);
    setError('');
  };

  const handleEditMediaSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const remaining = 5 - editMediaFiles.length;
    if (remaining <= 0) { setError('Maximum 5 files allowed'); return; }
    const filesToProcess = Array.from(files).slice(0, remaining);
    let errorMsg = '';
    filesToProcess.forEach(file => {
      if (file.size > 5 * 1024 * 1024) { errorMsg = 'Each file must be less than 5MB'; return; }
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');
      const fileType = isImage ? 'image' : isVideo ? 'video' : 'file';
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditMediaFiles(prev => prev.length >= 5 ? prev : [...prev, { url: reader.result as string, type: fileType as any, name: file.name }]);
      };
      reader.readAsDataURL(file);
    });
    if (errorMsg) setError(errorMsg); else setError('');
    if (editFileInputRef.current) editFileInputRef.current.value = '';
  };

  const handleEditRemoveMedia = (index: number) => {
    setEditMediaFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpdate = async () => {
    if (!editingAnnouncement) return;
    if (!editFormData.title.trim() || !editFormData.content.trim()) {
      setError('Please fill in title and content');
      return;
    }
    setUpdating(true);
    setError('');
    try {
      const payload: any = {
        title: editFormData.title,
        content: editFormData.content,
        priority: editFormData.priority,
        is_pinned: editFormData.is_pinned,
        media_files: editMediaFiles,
        media_url: editMediaFiles.length > 0 ? editMediaFiles[0].url : null,
        media_type: editMediaFiles.length > 0 ? editMediaFiles[0].type : null,
        link_url: editFormData.link_url || null,
        link_title: editFormData.link_url ? (editFormData.link_title || editFormData.link_url) : null,
      };
      await announcementsApi.update(editingAnnouncement.id, payload);
      setShowEditModal(false);
      setEditingAnnouncement(null);
      fetchAnnouncements();
    } catch (err: any) {
      console.error('Failed to update announcement:', err);
      setError(err.response?.data?.error || 'Failed to update announcement');
    } finally {
      setUpdating(false);
    }
  };

  // Comments functions
  const fetchComments = async (announcementId: string) => {
    setCommentsLoading(prev => new Set(prev).add(announcementId));
    try {
      const response = await announcementsApi.getComments(announcementId);
      setCommentsData(prev => ({ ...prev, [announcementId]: response.data }));
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    } finally {
      setCommentsLoading(prev => {
        const next = new Set(prev);
        next.delete(announcementId);
        return next;
      });
    }
  };

  const toggleComments = async (e: React.MouseEvent, announcementId: string) => {
    e.stopPropagation();
    const isExpanded = expandedComments.has(announcementId);
    if (isExpanded) {
      setExpandedComments(prev => {
        const next = new Set(prev);
        next.delete(announcementId);
        return next;
      });
    } else {
      setExpandedComments(prev => new Set(prev).add(announcementId));
      if (!commentsData[announcementId]) {
        await fetchComments(announcementId);
      }
      setTimeout(() => {
        commentInputRefs.current[announcementId]?.focus();
      }, 100);
    }
  };

  const handleSubmitComment = async (announcementId: string) => {
    const content = newComment[announcementId]?.trim();
    if (!content) return;
    setSubmittingComment(prev => new Set(prev).add(announcementId));
    try {
      const response = await announcementsApi.addComment(announcementId, content);
      setCommentsData(prev => ({
        ...prev,
        [announcementId]: [...(prev[announcementId] || []), response.data]
      }));
      setAnnouncements(prev => prev.map(a =>
        a.id === announcementId
          ? { ...a, comment_count: (a.comment_count || 0) + 1 }
          : a
      ));
      setNewComment(prev => ({ ...prev, [announcementId]: '' }));
    } catch (error) {
      console.error('Failed to add comment:', error);
    } finally {
      setSubmittingComment(prev => {
        const next = new Set(prev);
        next.delete(announcementId);
        return next;
      });
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent, announcementId: string) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmitComment(announcementId);
    }
  };

  const handleDeleteComment = async (announcementId: string, commentId: string) => {
    setDeletingComment(prev => new Set(prev).add(commentId));
    try {
      await announcementsApi.deleteComment(announcementId, commentId);
      setCommentsData(prev => ({
        ...prev,
        [announcementId]: prev[announcementId]?.filter(c => c.id !== commentId) || []
      }));
      setAnnouncements(prev => prev.map(a =>
        a.id === announcementId
          ? { ...a, comment_count: Math.max(0, (a.comment_count || 1) - 1) }
          : a
      ));
    } catch (error) {
      console.error('Failed to delete comment:', error);
    } finally {
      setDeletingComment(prev => {
        const next = new Set(prev);
        next.delete(commentId);
        return next;
      });
    }
  };

  const handleEditComment = (comment: Comment) => {
    setEditingCommentId(comment.id);
    setEditCommentText(comment.content);
  };

  const handleSaveEditComment = async (announcementId: string, commentId: string) => {
    if (!editCommentText.trim()) return;
    setSavingComment(true);
    try {
      const response = await announcementsApi.editComment(announcementId, commentId, editCommentText.trim());
      setCommentsData(prev => ({
        ...prev,
        [announcementId]: prev[announcementId]?.map(c =>
          c.id === commentId ? { ...c, content: response.data.content } : c
        ) || []
      }));
      setEditingCommentId(null);
      setEditCommentText('');
    } catch (error) {
      console.error('Failed to edit comment:', error);
    } finally {
      setSavingComment(false);
    }
  };

  const handleCommentReaction = async (announcementId: string, commentId: string, emoji: string) => {
    try {
      await announcementsApi.addCommentReaction(announcementId, commentId, emoji);
      const response = await announcementsApi.getComments(announcementId);
      setCommentsData(prev => ({ ...prev, [announcementId]: response.data }));
    } catch (err) { console.error(err); }
  };

  const handleReply = async (announcementId: string, parentId: string) => {
    if (!replyText.trim()) return;
    try {
      const response = await announcementsApi.addComment(announcementId, replyText.trim(), parentId);
      setCommentsData(prev => ({ ...prev, [announcementId]: [...(prev[announcementId] || []), response.data] }));
      setAnnouncements(prev => prev.map(a => a.id === announcementId ? { ...a, comment_count: (a.comment_count || 0) + 1 } : a));
      setReplyingToId(null);
      setReplyText('');
    } catch (err) { console.error(err); }
  };

  const handleReaction = async (e: React.MouseEvent, announcementId: string, emoji: string) => {
    e.stopPropagation();
    try {
      const response = await announcementsApi.addReaction(announcementId, emoji);
      const { action, previousEmoji } = response.data;
      setAnnouncements(prev =>
        prev.map(a => {
          if (a.id !== announcementId) return a;
          let reactions = [...(a.reactions || [])];
          if (action === 'removed') {
            reactions = reactions.map(r => r.emoji === emoji ? { ...r, count: r.count - 1 } : r).filter(r => r.count > 0);
          } else if (action === 'replaced') {
            // Decrement old, increment new
            reactions = reactions.map(r => r.emoji === previousEmoji ? { ...r, count: r.count - 1 } : r).filter(r => r.count > 0);
            const existing = reactions.find(r => r.emoji === emoji);
            if (existing) {
              reactions = reactions.map(r => r.emoji === emoji ? { ...r, count: r.count + 1 } : r);
            } else {
              reactions.push({ emoji, count: 1 });
            }
          } else {
            // added
            const existing = reactions.find(r => r.emoji === emoji);
            if (existing) {
              reactions = reactions.map(r => r.emoji === emoji ? { ...r, count: r.count + 1 } : r);
            } else {
              reactions.push({ emoji, count: 1 });
            }
          }
          return { ...a, reactions };
        })
      );
    } catch (error) {
      console.error('Failed to add reaction:', error);
    }
  };

  const formatCommentDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const handleCardClick = (announcement: Announcement) => {
    // Toggle expand on click, double-click opens modal
    const isExpanded = expandedCard === announcement.id;
    setExpandedCard(isExpanded ? null : announcement.id);

    // When collapsing, also close comments
    if (isExpanded) {
      setExpandedComments(prev => { const next = new Set(prev); next.delete(announcement.id); return next; });
    }

    // Mark as read when expanding for the first time
    if (!isExpanded && !announcement.is_read) {
      announcementsApi.getById(announcement.id).then(() => {
        setAnnouncements(prev =>
          prev.map(a => a.id === announcement.id ? { ...a, is_read: true, read_count: a.read_count + 1 } : a)
        );
      }).catch(() => {});
    }
  };

  const handleCardDoubleClick = (announcement: Announcement) => {
    handleViewAnnouncement(announcement);
  };

  // Helper to get media files from an announcement
  const getMediaFiles = (announcement: Announcement): MediaFile[] => {
    if (announcement.media_files && announcement.media_files.length > 0) {
      return announcement.media_files;
    }
    if (announcement.media_url) {
      return [{ url: announcement.media_url, type: (announcement.media_type || 'image') as 'image' | 'video' }];
    }
    return [];
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div>
          <h1>Announcements</h1>
          <p>{filteredAnnouncements.length} {timePeriod === '7' ? 'in last 7 days' : timePeriod === '30' ? 'this month' : timePeriod === '90' ? 'in last 90 days' : 'in selected period'} · {filteredAnnouncements.filter(a => a.is_pinned).length} pinned · {filteredAnnouncements.filter(a => a.priority === 'urgent').length === 0 ? 'nothing urgent' : filteredAnnouncements.filter(a => a.priority === 'urgent').length + ' urgent'}</p>
        </div>
        {canCreate && (
          <button className={styles.createBtn} onClick={() => setShowCreateModal(true)}>
            <Megaphone size={14} />
            New announcement
          </button>
        )}
      </div>

      {/* Filters Row */}
      <div className={styles.filtersRow}>
        <div className={styles.timeFilterGroup}>
          <button
            className={`${styles.timeFilterBtn} ${timePeriod === '7' ? styles.active : ''}`}
            onClick={() => handleTimePeriodChange('7')}
          >
            7 Days
          </button>
          <button
            className={`${styles.timeFilterBtn} ${timePeriod === '30' ? styles.active : ''}`}
            onClick={() => handleTimePeriodChange('30')}
          >
            30 Days
          </button>
          <button
            className={`${styles.timeFilterBtn} ${timePeriod === '90' ? styles.active : ''}`}
            onClick={() => handleTimePeriodChange('90')}
          >
            90 Days
          </button>
          <button
            className={`${styles.timeFilterBtn} ${timePeriod === 'custom' || showCustomDatePicker ? styles.active : ''}`}
            onClick={() => handleTimePeriodChange('custom')}
          >
            Custom
          </button>
        </div>

        <div className={styles.searchWrapper}>
          <Search size={16} className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search announcements..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
          {searchTerm && (
            <button className={styles.clearSearch} onClick={() => setSearchTerm('')}>×</button>
          )}
        </div>
      </div>

      {showCustomDatePicker && (
        <div className={styles.customDatePicker}>
          <input
            type="date"
            value={customDates.start}
            onChange={(e) => setCustomDates({ ...customDates, start: e.target.value })}
            className={styles.dateInput}
          />
          <span>to</span>
          <input
            type="date"
            value={customDates.end}
            onChange={(e) => setCustomDates({ ...customDates, end: e.target.value })}
            className={styles.dateInput}
          />
          <Button size="sm" onClick={handleCustomDateApply}>Apply</Button>
        </div>
      )}

      {loading ? (
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <span>Loading announcements...</span>
        </div>
      ) : filteredAnnouncements.length === 0 ? (
        <div className={styles.empty}>
          <Megaphone size={48} />
          <h3>No announcements found</h3>
          <p>Try adjusting your filters or time period</p>
        </div>
      ) : (
        <div className={styles.mainGrid}>
          {/* Cards Column */}
          <div className={styles.cardList}>
            {filteredAnnouncements.map((announcement) => {
              const p = PRIORITY_CONFIG[announcement.priority] || PRIORITY_CONFIG.medium;
              const isExpanded = expandedCard === announcement.id;

              const kind = getAnnouncementKind(announcement.title);

              return (
                <div
                  key={announcement.id}
                  data-announcement-id={announcement.id}
                  className={`${styles.annCard} ${announcement.is_pinned ? styles.pinned : ''} ${highlightId === announcement.id ? styles.highlighted : ''}`}
                  onClick={() => handleCardClick(announcement)}
                  onDoubleClick={() => handleCardDoubleClick(announcement)}
                >
                  {/* Colored Band */}
                  <div className={styles.annHero} style={announcement.is_pinned
                    ? { background: 'rgba(255,255,255,0.05)', borderBottomColor: 'rgba(255,255,255,0.12)' }
                    : { background: `linear-gradient(90deg, ${kind.bg}, ${kind.bg}80)`, borderBottomColor: `${kind.color}2b` }
                  }>
                    <div className={styles.annHeroTags}>
                      <span className={styles.annKindTag} style={announcement.is_pinned
                        ? { color: '#7fe3c9', background: 'rgba(127,227,201,0.14)' }
                        : { color: kind.color, background: kind.bg }
                      }>
                        {kind.label}
                      </span>
                      {(announcement.priority === 'urgent' || announcement.priority === 'high') && (
                        <span className={styles.annKindTag} style={{ color: p.color, background: p.bg }}>
                          {p.label.toUpperCase()}
                        </span>
                      )}
                      {announcement.is_pinned && (
                        <span className={styles.annPinnedTag}>PINNED</span>
                      )}
                    </div>
                    <div className={styles.annHeroRight}>
                      <span className={styles.annBandTime} style={announcement.is_pinned ? { color: 'rgba(255,255,255,0.45)' } : {}}>
                        {announcement.author_first_name} {announcement.author_last_name} · {formatTime(announcement.created_at)}
                      </span>
                      <button
                        className={styles.annExpandBtn}
                        onClick={(e) => { e.stopPropagation(); handleCardClick(announcement); }}
                        title={isExpanded ? 'Collapse' : 'Expand'}
                      >
                        {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </button>
                    </div>
                  </div>

                  {/* Body */}
                  <div className={styles.annBodyWrap} style={announcement.is_pinned ? { color: '#fff' } : {}}>
                    <h3 className={styles.annTitle} style={announcement.is_pinned
                      ? { font: '400 24px/1.28 var(--font-serif)', color: '#fff' }
                      : {}
                    }>{announcement.title}</h3>
                  </div>

                  {/* Body */}
                  <div className={styles.annBody}>
                    <div className={styles.annBodyContent}>
                      <div className={styles.annBodyText}>
                        <p className={`${styles.annPreview} ${isExpanded ? styles.annPreviewExpanded : styles.annPreviewClamped}`}>
                          <FormattedText text={announcement.content} />
                        </p>
                      </div>
                      {/* Thumbnail on the right (first image from media_files or legacy) */}
                      {(() => {
                        const files = getMediaFiles(announcement);
                        const firstImage = files.find(f => f.type === 'image');
                        return firstImage ? (
                          <div
                            className={styles.annThumb}
                            onClick={(e) => {
                              e.stopPropagation();
                              setLightboxMedia(files);
                              setLightboxIndex(0);
                              setLightboxOpen(true);
                            }}
                          >
                            <img src={firstImage.url} alt="" className={styles.annThumbImage} />
                            {files.length > 1 && <span className={styles.annThumbCount}>+{files.length - 1}</span>}
                          </div>
                        ) : null;
                      })()}
                    </div>
                  </div>

                  {/* Engagement summary (reactions) — clickable to open modal */}
                  {announcement.reactions && announcement.reactions.length > 0 && (
                    <div className={styles.annEngagement} onClick={(e) => {
                      e.stopPropagation();
                      const allUsers = announcement.reactions?.flatMap((r: any) =>
                        (r.users || []).map((u: any) => ({ ...u, emoji: r.emoji }))
                      ) || [];
                      setReactionsModalData(allUsers);
                      setShowReactionsModal(true);
                    }}>
                      <div className={styles.reactionSummary}>
                        {announcement.reactions.map((reaction) => (
                          <span key={reaction.emoji} className={styles.reactionChip}>
                            {reaction.emoji} {reaction.count}
                            {reaction.users && reaction.users.length > 0 && (
                              <div className={styles.reactionTooltip}>
                                {reaction.users.slice(0, 10).map((u, i) => (
                                  <div key={i} className={styles.reactionUser}>{u.first_name} {u.last_name}</div>
                                ))}
                                {reaction.users.length > 10 && (
                                  <div className={styles.reactionUser}>+{reaction.users.length - 10} more</div>
                                )}
                              </div>
                            )}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Footer */}
                  <div className={styles.annFooter} onClick={(e) => e.stopPropagation()}>
                    <div className={styles.annFooterLeft}>
                      {announcement.link_url && (
                        <a
                          href={announcement.link_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.annLinkPill}
                          onClick={(e) => e.stopPropagation()}
                          style={announcement.is_pinned ? { color: '#7fe3c9', borderColor: 'rgba(127,227,201,0.35)' } : {}}
                        >
                          <ExternalLink size={13} />
                          <span>{announcement.link_title || 'Link'}</span>
                        </a>
                      )}
                      {(() => {
                        const files = getMediaFiles(announcement);
                        const docFiles = files.filter(f => f.type !== 'image' && f.type !== 'video');
                        return docFiles.map((mf, i) => (
                          <a key={i} href={mf.url} download={mf.name || 'file'} className={styles.annLinkPill} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                            <FileText size={13} />
                            <span>Download file</span>
                          </a>
                        ));
                      })()}
                      <span
                        className={`${styles.annFooterStat} ${styles.annFooterStatClickable}`}
                        onClick={(e) => handleViewReaders(e, announcement)}
                        title="Click to see who viewed"
                      >
                        <Eye size={14} /> {announcement.read_count} views
                      </span>
                      {(announcement.reactions?.reduce((sum, r) => sum + r.count, 0) || 0) > 0 && (
                        <span
                          className={`${styles.annFooterStat} ${styles.annFooterStatClickable}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            const allUsers = announcement.reactions?.flatMap((r: any) =>
                              (r.users || []).map((u: any) => ({ ...u, emoji: r.emoji }))
                            ) || [];
                            setReactionsModalData(allUsers);
                            setShowReactionsModal(true);
                          }}
                          title="Click to see who reacted"
                        >
                          <Smile size={14} /> {announcement.reactions?.reduce((sum, r) => sum + r.count, 0)} reactions
                        </span>
                      )}
                    </div>
                    <div className={styles.annFooterRight}>
                      <div className={styles.likeActionWrapper}>
                        <button className={styles.annActionBtn} onClick={(e) => { e.stopPropagation(); }}>
                          <Smile size={15} />
                          <span>React</span>
                        </button>
                        <div className={styles.likeActionDropdown}>
                          {reactionEmojis.map((emoji) => (
                            <button key={emoji} className={styles.emojiOption} onClick={(e) => handleReaction(e, announcement.id, emoji)}>
                              {emoji}
                            </button>
                          ))}
                        </div>
                      </div>
                      <button className={styles.annActionBtn} onClick={(e) => toggleComments(e, announcement.id)}>
                        <MessageCircle size={15} />
                        <span>Comment{(announcement.comment_count || 0) > 0 ? ` (${announcement.comment_count})` : ''}</span>
                      </button>
                      {(canEditAnnouncement(announcement) || canPin || canDelete) && (
                        <span className={styles.annFooterDivider} />
                      )}
                      {canEditAnnouncement(announcement) && (
                        <button
                          className={`${styles.annIconBtn} ${styles.annIconBtnEdit}`}
                          onClick={(e) => handleEditClick(e, announcement)}
                          title="Edit"
                        >
                          <Edit2 size={14} />
                        </button>
                      )}
                      {canPin && (
                        <button
                          className={`${styles.annIconBtn} ${styles.annIconBtnPin} ${announcement.is_pinned ? styles.annIconBtnPinActive : ''}`}
                          onClick={(e) => handleTogglePin(e, announcement)}
                          title={announcement.is_pinned ? 'Unpin' : 'Pin'}
                        >
                          {announcement.is_pinned ? <StarOff size={14} /> : <Star size={14} />}
                        </button>
                      )}
                      {canDelete && (
                        <button
                          className={`${styles.annIconBtn} ${styles.annIconBtnDanger}`}
                          onClick={(e) => handleDeleteClick(e, announcement.id)}
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>

                    {/* Comments Section */}
                    {expandedComments.has(announcement.id) && (
                      <div className={styles.commentsSection} onClick={(e) => e.stopPropagation()}>
                        {commentsLoading.has(announcement.id) ? (
                          <div className={styles.commentsLoading}>
                            <div className={styles.smallSpinner}></div>
                            <span>Loading comments...</span>
                          </div>
                        ) : (
                          <>
                            {(commentsData[announcement.id] || []).length > 0 && (() => {
                              const allComments = commentsData[announcement.id] || [];
                              const topLevelComments = allComments.filter(c => !c.parent_id);
                              const getReplies = (parentId: string) => allComments.filter(c => c.parent_id === parentId);

                              const renderComment = (comment: Comment, isReply = false) => (
                                <div key={comment.id} className={styles.commentItem}>
                                  <Avatar
                                    src={comment.avatar_url}
                                    name={`${comment.first_name} ${comment.last_name}`}
                                    size="sm"
                                  />
                                  <div className={styles.commentContent}>
                                    <div className={styles.commentHeader}>
                                      <span className={styles.commentAuthor}>
                                        {comment.first_name} {comment.last_name}
                                      </span>
                                      <span className={styles.commentTime}>
                                        {formatCommentDate(comment.created_at)}
                                      </span>
                                      {(user?.role === 'superadmin' || user?.id === comment.user_id) && editingCommentId !== comment.id && (
                                        <button
                                          className={styles.editCommentBtn}
                                          onClick={() => handleEditComment(comment)}
                                          title="Edit comment"
                                        >
                                          <Edit2 size={12} />
                                        </button>
                                      )}
                                      {user?.role === 'superadmin' && (
                                        <button
                                          className={styles.deleteCommentBtn}
                                          onClick={() => handleDeleteComment(announcement.id, comment.id)}
                                          disabled={deletingComment.has(comment.id)}
                                          title="Delete comment"
                                        >
                                          {deletingComment.has(comment.id) ? (
                                            <div className={styles.tinySpinner}></div>
                                          ) : (
                                            <Trash2 size={12} />
                                          )}
                                        </button>
                                      )}
                                    </div>
                                    {editingCommentId === comment.id ? (
                                      <div className={styles.editCommentRow}>
                                        <MentionInput
                                          mode="input"
                                          value={editCommentText}
                                          onChange={setEditCommentText}
                                          onSubmit={() => handleSaveEditComment(announcement.id, comment.id)}
                                          placeholder="Edit comment..."
                                          disabled={savingComment}
                                          className={styles.mentionInputWrap}
                                        />
                                        <button className={styles.saveEditBtn} onClick={() => handleSaveEditComment(announcement.id, comment.id)} disabled={savingComment || !editCommentText.trim()}>
                                          {savingComment ? <div className={styles.tinySpinner}></div> : <SendHorizontal size={14} />}
                                        </button>
                                        <button className={styles.cancelEditBtn} onClick={() => setEditingCommentId(null)}>
                                          <X size={14} />
                                        </button>
                                      </div>
                                    ) : (
                                      <p className={styles.commentText}><MentionText text={comment.content} /></p>
                                    )}
                                    {/* Comment reactions */}
                                    {comment.reactions && comment.reactions.length > 0 && (
                                      <div className={styles.commentReactions}>
                                        {comment.reactions.map((r) => (
                                          <span
                                            key={r.emoji}
                                            className={styles.commentReactionChip}
                                            onClick={() => handleCommentReaction(announcement.id, comment.id, r.emoji)}
                                          >
                                            {r.emoji} {r.count}
                                            {r.users && r.users.length > 0 && (
                                              <div className={styles.commentReactionTooltip}>
                                                {r.users.slice(0, 10).map((u, i) => (
                                                  <div key={i}>{u.first_name} {u.last_name}</div>
                                                ))}
                                                {r.users.length > 10 && <div>+{r.users.length - 10} more</div>}
                                              </div>
                                            )}
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                    {/* Action row */}
                                    {editingCommentId !== comment.id && (
                                      <div className={styles.commentActionRow}>
                                        <div
                                          className={styles.commentEmojiPicker}
                                          onMouseEnter={(e) => {
                                            if (hoverReactTimer.current) clearTimeout(hoverReactTimer.current);
                                            const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                            setHoverReactPos({ top: rect.top >= 50 ? rect.top - 42 : rect.bottom + 6, left: Math.max(8, Math.min(rect.left, window.innerWidth - 340)) });
                                            setHoverReactFor(comment.id);
                                          }}
                                          onMouseLeave={() => {
                                            hoverReactTimer.current = setTimeout(() => setHoverReactFor(null), 250);
                                          }}
                                        >
                                          <button className={styles.commentActionBtn}>
                                            <Smile size={12} /> React
                                          </button>
                                          {hoverReactFor === comment.id && hoverReactPos && (
                                            <div
                                              className={styles.commentEmojiDropdown}
                                              style={{ display: 'flex', position: 'fixed', top: hoverReactPos.top, left: hoverReactPos.left, bottom: 'auto' }}
                                            >
                                              {reactionEmojis.map((emoji) => (
                                                <button key={emoji} onClick={() => { handleCommentReaction(announcement.id, comment.id, emoji); setHoverReactFor(null); }}>
                                                  {emoji}
                                                </button>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                        {!isReply && (
                                          <button className={styles.commentActionBtn} onClick={() => { setReplyingToId(replyingToId === comment.id ? null : comment.id); setReplyText(''); }}>
                                            <MessageCircle size={12} /> Reply
                                          </button>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );

                              return (
                                <div className={styles.commentsList}>
                                  {topLevelComments.map((comment) => (
                                    <div key={comment.id}>
                                      {renderComment(comment)}
                                      {/* Replies */}
                                      {getReplies(comment.id).length > 0 && (
                                        <div className={styles.replyGroup}>
                                          {getReplies(comment.id).map((reply) => renderComment(reply, true))}
                                        </div>
                                      )}
                                      {/* Reply input */}
                                      {replyingToId === comment.id && (
                                        <div className={styles.replyInputRow}>
                                          <div className={styles.commentInputContainer}>
                                            <MentionInput
                                            mode="input"
                                            placeholder={`Reply to ${comment.first_name}...`}
                                            value={replyText}
                                            onChange={setReplyText}
                                            onSubmit={() => handleReply(announcement.id, comment.id)}
                                            className={styles.mentionInputWrap}
                                          />
                                            <button className={styles.sendCommentBtn} onClick={() => handleReply(announcement.id, comment.id)} disabled={!replyText.trim()}>
                                            <SendHorizontal size={14} />
                                          </button>
                                          </div>
                                          <button className={styles.cancelEditBtn} onClick={() => { setReplyingToId(null); setReplyText(''); }}>
                                            <X size={14} />
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              );
                            })()}

                            {(commentsData[announcement.id] || []).length === 0 && (
                              <div className={styles.noComments}>
                                <MessageCircle size={20} />
                                <span>No comments yet. Be the first to comment!</span>
                              </div>
                            )}

                            <div className={styles.commentInputWrapper}>
                              <Avatar
                                src={user?.avatar_url}
                                name={`${user?.first_name} ${user?.last_name}`}
                                size="sm"
                              />
                              <div className={styles.commentInputContainer}>
                                <MentionInput
                                  mode="input"
                                  placeholder="Write a comment... Use @ to mention"
                                  value={newComment[announcement.id] || ''}
                                  onChange={(val) => setNewComment(prev => ({
                                    ...prev,
                                    [announcement.id]: val
                                  }))}
                                  onSubmit={() => handleSubmitComment(announcement.id)}
                                  disabled={submittingComment.has(announcement.id)}
                                  className={styles.mentionInputWrap}
                                />
                                <button
                                  className={styles.sendCommentBtn}
                                  onClick={() => handleSubmitComment(announcement.id)}
                                  disabled={!newComment[announcement.id]?.trim() || submittingComment.has(announcement.id)}
                                >
                                  {submittingComment.has(announcement.id) ? (
                                    <div className={styles.tinySpinner}></div>
                                  ) : (
                                    <SendHorizontal size={16} />
                                  )}
                                </button>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                </div>
              );
            })}
          </div>

          {/* Sidebar */}
          <div className={styles.sidebar}>
            {/* Overview */}
            <div className={styles.sidebarCard}>
              <h3 className={styles.sidebarTitle}>Overview</h3>
              <div className={styles.overviewList}>
                {overviewStats.map((s, i) => (
                  <div key={i} className={styles.overviewItem}>
                    <span className={styles.overviewLabel}>{s.label}</span>
                    <span className={styles.overviewValue} style={{ color: s.color }}>{s.value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Activity */}
            <div className={styles.sidebarCard}>
              <h3 className={styles.sidebarTitle}>Recent Activity</h3>
              <div className={styles.activityList}>
                {recentActivity.map(a => {
                  const ap = PRIORITY_CONFIG[a.priority] || PRIORITY_CONFIG.medium;
                  return (
                    <div key={a.id} className={styles.activityItem}>
                      <div className={styles.activityDot} style={{ background: ap.dot }} />
                      <div className={styles.activityInfo}>
                        <div className={styles.activityTitle}>{a.title}</div>
                        <div className={styles.activityMeta}>{formatTime(a.created_at)} · {a.read_count} views</div>
                      </div>
                    </div>
                  );
                })}
                {recentActivity.length === 0 && (
                  <div style={{ fontSize: 13, color: '#94A3B8', textAlign: 'center', padding: 12 }}>
                    No recent activity
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        size="lg"
      >
        {/* Custom Hero Header */}
        <div className={styles.modalHero}>
          <div className={styles.modalHeroLeft}>
            <div className={styles.heroIconBox}>
              <Megaphone size={16} color="#9FE1CB" />
            </div>
            <div>
              <div className={styles.modalTitle}>Create Announcement</div>
              <div className={styles.modalSub}>Broadcast a message to your organization</div>
            </div>
          </div>
          <button className={styles.modalCloseBtn} onClick={() => setShowCreateModal(false)}>
            <X size={14} />
          </button>
        </div>

        <div className={styles.form} style={{ padding: '18px 22px' }}>
          {/* Title */}
          <div>
            <label className={styles.fieldLabel}>
              Title
            </label>
            <input
              className={styles.fieldInput}
              placeholder="Announcement title..."
              value={newAnnouncement.title}
              onChange={(e) => setNewAnnouncement({ ...newAnnouncement, title: e.target.value })}
            />
          </div>

          {/* Content */}
          <div>
            <label className={styles.fieldLabel}>Content</label>
            <RichTextEditor
              value={newAnnouncement.content}
              onChange={(val) => setNewAnnouncement(prev => ({ ...prev, content: val }))}
              placeholder="Write your announcement…"
              minHeight={160}
            />
          </div>

          {/* Media Upload Section */}
          <div className={styles.mediaSection}>
            <label className={styles.fieldLabel}>
              Attachments <span className={styles.fieldOptional}>Optional</span> - {createMediaFiles.length}/5
            </label>
            <input type="file" ref={fileInputRef} accept="image/*,video/*" multiple onChange={handleMediaSelect} style={{ display: 'none' }} />
            <input type="file" ref={fileDocInputRef} accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip" multiple onChange={handleMediaSelect} style={{ display: 'none' }} />
            {createMediaFiles.length > 0 && (
              <div className={styles.uploadPreviewGrid}>
                {createMediaFiles.map((mf, i) => (
                  <div key={i} className={styles.uploadPreviewItem}>
                    {mf.type === 'image' ? (
                      <img src={mf.url} alt={mf.name || 'Preview'} />
                    ) : mf.type === 'video' ? (
                      <video src={mf.url} />
                    ) : (
                      <div className={styles.filePreview}><FileText size={18} /><span>{mf.name || 'File'}</span></div>
                    )}
                    <button className={styles.uploadRemoveBtn} onClick={() => handleRemoveMedia(i)} type="button">
                      <X size={10} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {createMediaFiles.length < 5 && (
              <div className={styles.mediaButtons}>
                <button type="button" className={styles.mediaButton} onClick={() => fileInputRef.current?.click()}>
                  <Image size={18} />
                  <span>Upload media</span>
                </button>
                <button type="button" className={styles.mediaButton} onClick={() => fileDocInputRef.current?.click()}>
                  <FileText size={18} />
                  <span>Upload file</span>
                </button>
              </div>
            )}
          </div>

          {/* Link Section */}
          <div className={styles.linkSection}>
            <div>
              <label className={styles.fieldLabel}>
                Link <span className={styles.fieldOptional}>Optional</span>
              </label>
              <input
                className={styles.fieldInput}
                placeholder="https://example.com"
                value={newAnnouncement.link_url}
                onChange={(e) => setNewAnnouncement({ ...newAnnouncement, link_url: e.target.value })}
              />
            </div>
            {newAnnouncement.link_url && (
              <div>
                <label className={styles.fieldLabel}>Link Title</label>
                <input
                  className={styles.fieldInput}
                  placeholder="Link display text..."
                  value={newAnnouncement.link_title}
                  onChange={(e) => setNewAnnouncement({ ...newAnnouncement, link_title: e.target.value })}
                />
              </div>
            )}
          </div>

          {/* Priority Toggle Buttons */}
          <div>
            <label className={styles.fieldLabel}>Priority</label>
            <div className={styles.priorityRow}>
              <button
                type="button"
                className={`${styles.priBtn} ${newAnnouncement.priority === 'low' ? styles.priBtnLow : ''}`}
                onClick={() => setNewAnnouncement({ ...newAnnouncement, priority: 'low' })}
              >
                Low
              </button>
              <button
                type="button"
                className={`${styles.priBtn} ${newAnnouncement.priority === 'medium' ? styles.priBtnMedium : ''}`}
                onClick={() => setNewAnnouncement({ ...newAnnouncement, priority: 'medium' })}
              >
                Medium
              </button>
              <button
                type="button"
                className={`${styles.priBtn} ${newAnnouncement.priority === 'high' ? styles.priBtnHigh : ''}`}
                onClick={() => setNewAnnouncement({ ...newAnnouncement, priority: 'high' })}
              >
                High
              </button>
            </div>
          </div>

          {/* Pin Toggle Card */}
          <div
            className={`${styles.pinCard} ${newAnnouncement.is_pinned ? styles.pinCardActive : ''}`}
            onClick={() => setNewAnnouncement({ ...newAnnouncement, is_pinned: !newAnnouncement.is_pinned })}
          >
            <div className={styles.pinCardLeft}>
              <div className={styles.pinIconWrap}>
                <Pin size={14} color={newAnnouncement.is_pinned ? '#fff' : '#4A6B5E'} />
              </div>
              <div>
                <div className={styles.pinLabel}>Pin Announcement</div>
                <div className={styles.pinSub}>Pinned announcements stay at the top</div>
              </div>
            </div>
            <div className={styles.toggleSwitch} />
          </div>

          {error && <div className={styles.error}>{error}</div>}
        </div>

        {/* Modal Footer */}
        <div className={styles.modalFooter}>
          <div className={styles.footerHint}>
            <Eye size={12} /> Visible to all members
          </div>
          <div className={styles.footerActions}>
            <button className={styles.btnCancel} onClick={() => setShowCreateModal(false)}>
              Cancel
            </button>
            <button
              className={styles.btnPublish}
              onClick={handleCreate}
              disabled={!newAnnouncement.title.trim() || !newAnnouncement.content.trim() || creating}
            >
              {creating ? 'Publishing...' : 'Publish'}
              {!creating && <SendHorizontal size={13} />}
            </button>
          </div>
        </div>
      </Modal>

      {/* View Modal */}
      <Modal
        isOpen={!!selectedAnnouncement}
        onClose={() => setSelectedAnnouncement(null)}
        title={selectedAnnouncement?.title || ''}
        size="md"
      >
        {selectedAnnouncement && (
          <div className={styles.viewModal}>
            <div className={styles.viewHeader}>
              <div className={styles.viewBadges}>
                {(() => {
                  const vp = PRIORITY_CONFIG[selectedAnnouncement.priority] || PRIORITY_CONFIG.medium;
                  return (
                    <span
                      className={styles.priorityBadge}
                      style={{ color: vp.color, background: vp.bg, border: `1px solid ${vp.border}` }}
                    >
                      <span className={styles.priorityDot} style={{ background: vp.dot }} />
                      {vp.label}
                    </span>
                  );
                })()}
                {selectedAnnouncement.is_pinned && (
                  <span className={styles.pinnedBadge}>
                    <Star size={11} /> Pinned
                  </span>
                )}
              </div>
              <div className={styles.viewMeta}>
                <Calendar size={14} />
                {formatDate(selectedAnnouncement.created_at)}
              </div>
            </div>
            <div className={styles.viewContentRow}>
              {(() => {
                const files = getMediaFiles(selectedAnnouncement);
                const firstImage = files.find(f => f.type === 'image');
                return firstImage ? (
                  <div
                    className={styles.viewThumb}
                    onClick={() => {
                      setLightboxMedia(files);
                      setLightboxIndex(0);
                      setLightboxOpen(true);
                    }}
                  >
                    <img src={firstImage.url} alt="" className={styles.viewThumbImage} />
                  </div>
                ) : null;
              })()}
              <div className={styles.viewContent}>
                {selectedAnnouncement.content}
              </div>
            </div>

            {selectedAnnouncement.link_url && (
              <a
                href={selectedAnnouncement.link_url}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.viewLink}
              >
                <ExternalLink size={16} />
                <span>{selectedAnnouncement.link_title || selectedAnnouncement.link_url}</span>
              </a>
            )}
            <div className={styles.viewFooter}>
              <div className={styles.authorSection}>
                <Avatar
                  src={selectedAnnouncement.author_avatar}
                  name={`${selectedAnnouncement.author_first_name} ${selectedAnnouncement.author_last_name}`}
                  size="md"
                />
                <span className={styles.authorName}>
                  {selectedAnnouncement.author_first_name} {selectedAnnouncement.author_last_name}
                </span>
              </div>
              <div className={styles.viewActions}>
                {selectedAnnouncement && canEditAnnouncement(selectedAnnouncement) && (
                  <button
                    className={styles.viewEditBtn}
                    onClick={(e) => handleEditClick(e, selectedAnnouncement)}
                  >
                    <Edit2 size={16} />
                    Edit
                  </button>
                )}
                {canPin && (
                  <button
                    className={`${styles.viewPinBtn} ${selectedAnnouncement.is_pinned ? styles.unpinBtn : ''}`}
                    onClick={(e) => handleTogglePin(e, selectedAnnouncement)}
                  >
                    {selectedAnnouncement.is_pinned ? <StarOff size={16} /> : <Star size={16} />}
                    {selectedAnnouncement.is_pinned ? 'Unpin' : 'Pin'}
                  </button>
                )}
                {canDelete && (
                  <button
                    className={styles.viewDeleteBtn}
                    onClick={() => handleDeleteClick(
                      { stopPropagation: () => {} } as React.MouseEvent,
                      selectedAnnouncement.id
                    )}
                  >
                    <Trash2 size={16} />
                    Delete
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setDeleteId(null);
        }}
        title="Delete Announcement"
      >
        <div className={styles.deleteModal}>
          <p>Are you sure you want to delete this announcement?</p>
          <p className={styles.deleteWarning}>
            This action cannot be undone. The announcement will be permanently removed.
          </p>
          <div className={styles.deleteActions}>
            <Button
              variant="ghost"
              onClick={() => {
                setDeleteModalOpen(false);
                setDeleteId(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleDeleteConfirm}
              disabled={deleting}
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Edit Announcement Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => { setShowEditModal(false); setEditingAnnouncement(null); }}
        size="lg"
      >
        {/* Custom Hero Header */}
        <div className={styles.modalHero}>
          <div className={styles.modalHeroLeft}>
            <div className={styles.heroIconBox}>
              <Edit2 size={16} color="#9FE1CB" />
            </div>
            <div>
              <div className={styles.modalTitle}>Edit Announcement</div>
              <div className={styles.modalSub}>Update your announcement details</div>
            </div>
          </div>
          <button className={styles.modalCloseBtn} onClick={() => { setShowEditModal(false); setEditingAnnouncement(null); }}>
            <X size={14} />
          </button>
        </div>

        <div className={styles.form} style={{ padding: '18px 22px' }}>
          {/* Title */}
          <div>
            <label className={styles.fieldLabel}>Title</label>
            <input
              className={styles.fieldInput}
              placeholder="Announcement title..."
              value={editFormData.title}
              onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
            />
          </div>

          {/* Content */}
          <div>
            <label className={styles.fieldLabel}>Content</label>
            <RichTextEditor
              value={editFormData.content}
              onChange={(val) => setEditFormData(prev => ({ ...prev, content: val }))}
              placeholder="Write your announcement…"
              minHeight={160}
            />
          </div>

          {/* Media Upload Section */}
          <div className={styles.mediaSection}>
            <label className={styles.fieldLabel}>
              Attachments <span className={styles.fieldOptional}>Optional</span> - {editMediaFiles.length}/5
            </label>
            <input type="file" ref={editFileInputRef} accept="image/*,video/*" multiple onChange={handleEditMediaSelect} style={{ display: 'none' }} />
            <input type="file" ref={editFileDocInputRef} accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip" multiple onChange={handleEditMediaSelect} style={{ display: 'none' }} />
            {editMediaFiles.length > 0 && (
              <div className={styles.uploadPreviewGrid}>
                {editMediaFiles.map((mf, i) => (
                  <div key={i} className={styles.uploadPreviewItem}>
                    {mf.type === 'image' ? (
                      <img src={mf.url} alt={mf.name || 'Preview'} />
                    ) : mf.type === 'video' ? (
                      <video src={mf.url} />
                    ) : (
                      <div className={styles.filePreview}><FileText size={18} /><span>{mf.name || 'File'}</span></div>
                    )}
                    <button className={styles.uploadRemoveBtn} onClick={() => handleEditRemoveMedia(i)} type="button">
                      <X size={10} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {editMediaFiles.length < 5 && (
              <div className={styles.mediaButtons}>
                <button type="button" className={styles.mediaButton} onClick={() => editFileInputRef.current?.click()}>
                  <Image size={18} />
                  <span>Upload media</span>
                </button>
                <button type="button" className={styles.mediaButton} onClick={() => editFileDocInputRef.current?.click()}>
                  <FileText size={18} />
                  <span>Upload file</span>
                </button>
              </div>
            )}
          </div>

          {/* Link Section */}
          <div className={styles.linkSection}>
            <div>
              <label className={styles.fieldLabel}>
                Link <span className={styles.fieldOptional}>Optional</span>
              </label>
              <input
                className={styles.fieldInput}
                placeholder="https://example.com"
                value={editFormData.link_url}
                onChange={(e) => setEditFormData({ ...editFormData, link_url: e.target.value })}
              />
            </div>
            {editFormData.link_url && (
              <div>
                <label className={styles.fieldLabel}>Link Title</label>
                <input
                  className={styles.fieldInput}
                  placeholder="Link display text..."
                  value={editFormData.link_title}
                  onChange={(e) => setEditFormData({ ...editFormData, link_title: e.target.value })}
                />
              </div>
            )}
          </div>

          {/* Priority Toggle Buttons */}
          <div>
            <label className={styles.fieldLabel}>Priority</label>
            <div className={styles.priorityRow}>
              <button
                type="button"
                className={`${styles.priBtn} ${editFormData.priority === 'low' ? styles.priBtnLow : ''}`}
                onClick={() => setEditFormData({ ...editFormData, priority: 'low' })}
              >
                Low
              </button>
              <button
                type="button"
                className={`${styles.priBtn} ${editFormData.priority === 'medium' ? styles.priBtnMedium : ''}`}
                onClick={() => setEditFormData({ ...editFormData, priority: 'medium' })}
              >
                Medium
              </button>
              <button
                type="button"
                className={`${styles.priBtn} ${editFormData.priority === 'high' ? styles.priBtnHigh : ''}`}
                onClick={() => setEditFormData({ ...editFormData, priority: 'high' })}
              >
                High
              </button>
            </div>
          </div>

          {/* Pin Toggle Card */}
          <div
            className={`${styles.pinCard} ${editFormData.is_pinned ? styles.pinCardActive : ''}`}
            onClick={() => setEditFormData({ ...editFormData, is_pinned: !editFormData.is_pinned })}
          >
            <div className={styles.pinCardLeft}>
              <div className={styles.pinIconWrap}>
                <Pin size={14} color={editFormData.is_pinned ? '#fff' : '#4A6B5E'} />
              </div>
              <div>
                <div className={styles.pinLabel}>Pin Announcement</div>
                <div className={styles.pinSub}>Pinned announcements stay at the top</div>
              </div>
            </div>
            <div className={styles.toggleSwitch} />
          </div>

          {error && <div className={styles.error}>{error}</div>}
        </div>

        {/* Modal Footer */}
        <div className={styles.modalFooter}>
          <div className={styles.footerHint}>
            <Eye size={12} /> Visible to all members
          </div>
          <div className={styles.footerActions}>
            <button className={styles.btnCancel} onClick={() => { setShowEditModal(false); setEditingAnnouncement(null); }}>
              Cancel
            </button>
            <button
              className={styles.btnPublish}
              onClick={handleUpdate}
              disabled={!editFormData.title.trim() || !editFormData.content.trim() || updating}
            >
              {updating ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Lightbox */}
      {lightboxOpen && (
        <div className={styles.lightboxOverlay} onClick={() => setLightboxOpen(false)}>
          <button className={styles.lightboxClose} onClick={() => setLightboxOpen(false)}>&#10005;</button>
          {lightboxMedia.length > 1 && lightboxIndex > 0 && (
            <button
              className={`${styles.lightboxNav} ${styles.lightboxPrev}`}
              onClick={(e) => { e.stopPropagation(); setLightboxIndex(i => i - 1); }}
            >
              &#8249;
            </button>
          )}
          <div className={styles.lightboxContent} onClick={e => e.stopPropagation()}>
            {lightboxMedia[lightboxIndex]?.type === 'image' ? (
              <img src={lightboxMedia[lightboxIndex].url} alt="" />
            ) : (
              <video src={lightboxMedia[lightboxIndex].url} controls autoPlay />
            )}
          </div>
          {lightboxMedia.length > 1 && lightboxIndex < lightboxMedia.length - 1 && (
            <button
              className={`${styles.lightboxNav} ${styles.lightboxNext}`}
              onClick={(e) => { e.stopPropagation(); setLightboxIndex(i => i + 1); }}
            >
              &#8250;
            </button>
          )}
          {lightboxMedia.length > 1 && (
            <div className={styles.lightboxCounter}>{lightboxIndex + 1} / {lightboxMedia.length}</div>
          )}
        </div>
      )}

      {/* Viewed By Modal */}
      <Modal isOpen={showViewersModal} onClose={() => setShowViewersModal(false)} title={`Viewed By`}>
        <div className={styles.viewersModal}>
          <p className={styles.viewersSubtitle}>
            <Eye size={14} /> {viewersData.length} {viewersData.length === 1 ? 'person' : 'people'} viewed "{viewersTitle}"
          </p>
          {viewersLoading ? (
            <div className={styles.viewersLoading}>Loading...</div>
          ) : viewersData.length === 0 ? (
            <div className={styles.viewersEmpty}>No one has viewed this yet.</div>
          ) : (
            <div className={styles.viewersList}>
              {viewersData.map((viewer: any, idx: number) => (
                <div key={idx} className={styles.viewerRow}>
                  <Avatar
                    src={viewer.avatar_url}
                    name={`${viewer.first_name} ${viewer.last_name}`}
                    size="sm"
                  />
                  <div className={styles.viewerInfo}>
                    <span className={styles.viewerName}>{viewer.first_name} {viewer.last_name}</span>
                  </div>
                  <span className={styles.viewerTime}>
                    {new Date(viewer.read_at || viewer.viewed_at).toLocaleDateString('en-US', { day: 'numeric', month: 'short' })}
                    {' '}
                    {new Date(viewer.read_at || viewer.viewed_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>

      <Modal isOpen={showReactionsModal} onClose={() => setShowReactionsModal(false)} title="Reactions">
        <div style={{ minWidth: 280 }}>
          <p style={{ fontSize: 13, color: 'rgba(20,32,29,0.5)', marginBottom: 12, paddingBottom: 8, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
            {reactionsModalData.length} {reactionsModalData.length === 1 ? 'person' : 'people'} reacted
          </p>
          {reactionsModalData.map((reactor: any, idx: number) => (
            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0' }}>
              <Avatar src={reactor.avatar_url} name={`${reactor.first_name} ${reactor.last_name}`} size="sm" />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#14201d' }}>{reactor.first_name} {reactor.last_name}</div>
              </div>
              <span style={{ fontSize: 16 }}>{reactor.emoji}</span>
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
};

export default Announcements;
